import Header from '@/components/elements/Header/Header';
import { Switch } from '@/components/elements/forms/Switch';
import { ListItem, ListItemContent } from '@/components/elements/lists';
import { SCREEN_NAME, SCREEN_VIEW } from '@/constants/analytics';
import useMobileView from '@/hooks/useMobileView';
import useTrackScreenShown from '@/hooks/useTrackScreenShown';
import { _s } from '@/locale';
import Markdown from 'markdown-to-jsx';
import { useState } from 'react';
import * as sanitizeHtml from 'sanitize-html';
import Modal from '../../modals/redesign/Modal/Modal';

type BookingTermsProps = {
  terms: string | React.ReactNode;
  termsAccepted: boolean;
  error?: boolean;
  isCheckoutRedesign?: boolean;
  onChange: () => void;
  onClose: () => void;
};

const baseTranslationKey = 'components.modules.checkout.BookingTerms';

const BookingTerms = ({
  terms,
  termsAccepted,
  error,
  isCheckoutRedesign = false,
  onChange,
  onClose,
}: BookingTermsProps) => {
  const { isMobileView } = useMobileView();
  const [showTerms, setShowTerms] = useState(false);

  const markdown = sanitizeHtml(terms ?? '', {
    allowedTags: [],
    allowedAttributes: {},
  });

  useTrackScreenShown(
    { screen_name: SCREEN_VIEW.PRACTITIONER_TERMS, initiated_from: SCREEN_NAME.CHECKOUT_BOOKING_SUMMARY },
    [showTerms],
    [showTerms],
  );

  return (
    <div>
      <div className="px-lg py-md">
        <Header label={_s(`${baseTranslationKey}.title`)} size="lg" />
      </div>
      <ListItem
        className={`py-sm${error ? ' border-danger-500 border-b' : ''}`}
        rightSlot={
          <Switch
            switched={termsAccepted}
            onChange={onChange}
            data-cy="bookingTermsButton"
            isCheckoutRedesign={isCheckoutRedesign}
          />
        }>
        <ListItemContent
          subTitle={
            <span>
              {_s(`${baseTranslationKey}.description`)}
              <button onClick={() => setShowTerms(true)} className="text-information-700 inline underline">
                {_s(`${baseTranslationKey}.terms`)}
              </button>
            </span>
          }
        />
      </ListItem>
      {error && <p className="pl-lg pt-sm text-danger-500 text-xxs">{_s(`${baseTranslationKey}.error`)}</p>}
      <Modal isOpen={showTerms}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header
            title={_s(`${baseTranslationKey}.modal.title`)}
            onClose={() => {
              setShowTerms(false);
              onClose();
            }}
          />
          <div className="px-xxl py-md md:p-0">
            <Markdown options={{ wrapper: TermsWrapper }}>{markdown}</Markdown>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
};

const TermsWrapper = ({ children }) => {
  return <div className="space-y-sm relative flex flex-col">{children}</div>;
};

export default BookingTerms;
